// pages/dashboard/Settings.jsx
// Steve's settings page
// PayPal connection with two options:
// 1. Manual credentials (Client ID + Secret)
// 2. Shows OAuth option placeholder for future

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const Settings = () => {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // PayPal manual form
  const [paypalForm, setPaypalForm] = useState({
    clientId: '',
    clientSecret: '',
    mode: 'sandbox',
  });

  // General settings form
  const [generalForm, setGeneralForm] = useState({
    assessmentPrice: 149,
    calendlyLink: '',
    adminEmail: '',
    reportDeliveryMessage: '',
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/settings');
      if (response.data.success) {
        const data = response.data.data;
        setSettings(data);
        setGeneralForm({
          assessmentPrice: data.assessmentPrice,
          calendlyLink: data.calendlyLink,
          adminEmail: data.adminEmail,
          reportDeliveryMessage: data.reportDeliveryMessage,
        });
        if (data.paypal.clientId) {
          setPaypalForm(prev => ({ ...prev, clientId: data.paypal.clientId, mode: data.paypal.mode }));
        }
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Could not load settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleConnectPaypal = async (e) => {
    e.preventDefault();
    setConnecting(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await axios.post('/settings/paypal/connect-manual', paypalForm);
      if (response.data.success) {
        setMessage({ type: 'success', text: 'PayPal connected successfully!' });
        fetchSettings();
        setPaypalForm(prev => ({ ...prev, clientSecret: '' }));
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Could not connect PayPal. Please check your credentials.' });
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnectPaypal = async () => {
    if (!window.confirm('Are you sure you want to disconnect PayPal?')) return;
    try {
      await axios.post('/settings/paypal/disconnect');
      setMessage({ type: 'success', text: 'PayPal disconnected' });
      fetchSettings();
    } catch (err) {
      setMessage({ type: 'error', text: 'Could not disconnect PayPal' });
    }
  };

  const handleSaveGeneral = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await axios.put('/settings', generalForm);
      if (response.data.success) {
        setMessage({ type: 'success', text: 'Settings saved successfully!' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Could not save settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div style={styles.loadingPage}>
        <div style={styles.spinner} />
      </div>
    );
  }

  return (
    <div style={styles.page}>

      {/* Sidebar */}
      <div style={styles.sidebar}>
        <div style={styles.sidebarLogo}>
          <span style={styles.logoExec}>EXECUTIVE</span>
          <span style={styles.logoSpring}>Springboard</span>
        </div>
        <nav style={styles.nav}>
          {[
            { label: 'Dashboard', path: '/admin/dashboard', icon: '📊' },
            { label: 'Submissions', path: '/admin/submissions', icon: '📋' },
            { label: 'Transactions', path: '/admin/transactions', icon: '💳' },
            { label: 'Meetings', path: '/admin/meetings', icon: '📅' },
            { label: 'Enterprise', path: '/admin/enterprise', icon: '🏢' },
            { label: 'Form Maker', path: '/admin/forms', icon: '📝' },
            { label: 'Analytics', path: '/admin/analytics', icon: '📈' },
            { label: 'Settings', path: '/admin/settings', icon: '⚙️', active: true },
          ].map((item) => (
            <button
              key={item.path}
              style={{ ...styles.navItem, ...(item.active ? styles.navItemActive : {}) }}
              onClick={() => navigate(item.path)}
            >
              <span>{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
        <button style={styles.logoutBtn} onClick={handleLogout}>
          Sign Out
        </button>
      </div>

      {/* Main Content */}
      <div style={styles.main}>
        <div style={styles.mainHeader}>
          <h1 style={styles.pageTitle}>Settings</h1>
          <p style={styles.pageSubtitle}>Configure your platform settings and integrations</p>
        </div>

        {message.text && (
          <div style={{ ...styles.messageBox, ...(message.type === 'success' ? styles.messageSuccess : styles.messageError) }}>
            {message.text}
          </div>
        )}

        {/* PayPal Integration */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div>
              <h2 style={styles.cardTitle}>💳 PayPal Integration</h2>
              <p style={styles.cardSubtitle}>Connect your PayPal account to accept payments</p>
            </div>
            <div style={{
              ...styles.statusBadge,
              ...(settings?.paypal?.connected ? styles.statusConnected : styles.statusDisconnected)
            }}>
              {settings?.paypal?.connected ? '● Connected' : '○ Not Connected'}
            </div>
          </div>

          {settings?.paypal?.connected ? (
            // Connected state
            <div style={styles.connectedBox}>
              <div style={styles.connectedInfo}>
                <div style={styles.connectedRow}>
                  <span style={styles.connectedLabel}>Mode</span>
                  <span style={styles.connectedValue}>{settings.paypal.mode === 'live' ? '🟢 Live' : '🟡 Sandbox'}</span>
                </div>
                <div style={styles.connectedRow}>
                  <span style={styles.connectedLabel}>Client ID</span>
                  <span style={styles.connectedValue}>{settings.paypal.clientId?.substring(0, 20)}...</span>
                </div>
                {settings.paypal.connectedAt && (
                  <div style={styles.connectedRow}>
                    <span style={styles.connectedLabel}>Connected</span>
                    <span style={styles.connectedValue}>{new Date(settings.paypal.connectedAt).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
              <button style={styles.btnDanger} onClick={handleDisconnectPaypal}>
                Disconnect PayPal
              </button>
            </div>
          ) : (
            // Not connected state
            <div>
              {/* Option 1: Manual Credentials */}
              <div style={styles.connectOption}>
                <h3 style={styles.optionTitle}>Option 1: Connect with API Credentials</h3>
                <p style={styles.optionDesc}>
                  Enter your PayPal Client ID and Secret from your PayPal Developer Dashboard.
                  Go to <strong>developer.paypal.com</strong> → My Apps and Credentials → Create App
                </p>
                <form onSubmit={handleConnectPaypal}>
                  <div style={styles.formRow}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Mode</label>
                      <select
                        style={styles.select}
                        value={paypalForm.mode}
                        onChange={e => setPaypalForm(prev => ({ ...prev, mode: e.target.value }))}
                      >
                        <option value="sandbox">Sandbox (Testing)</option>
                        <option value="live">Live (Real Payments)</option>
                      </select>
                    </div>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Client ID *</label>
                    <input
                      type="text"
                      style={styles.input}
                      placeholder="AaBbCc..."
                      value={paypalForm.clientId}
                      onChange={e => setPaypalForm(prev => ({ ...prev, clientId: e.target.value }))}
                      required
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Client Secret *</label>
                    <input
                      type="password"
                      style={styles.input}
                      placeholder="Enter your PayPal Client Secret"
                      value={paypalForm.clientSecret}
                      onChange={e => setPaypalForm(prev => ({ ...prev, clientSecret: e.target.value }))}
                      required
                    />
                    <p style={styles.fieldNote}>Your secret is encrypted and stored securely. It is never shown again.</p>
                  </div>
                  <button type="submit" style={styles.btnConnect} disabled={connecting}>
                    {connecting ? 'Verifying and Connecting...' : 'Connect PayPal'}
                  </button>
                </form>
              </div>

              {/* Option 2: OAuth Login (Placeholder) */}
              <div style={styles.connectOptionAlt}>
                <h3 style={styles.optionTitle}>Option 2: Connect with PayPal Login</h3>
                <p style={styles.optionDesc}>
                  Sign in directly with your PayPal business account. No credentials needed.
                </p>
                <button style={styles.btnPaypalOAuth} disabled>
                  <span>🔒</span> Connect with PayPal Login (Coming Soon)
                </button>
                <p style={styles.fieldNote}>This option will be available once PayPal app approval is complete.</p>
              </div>
            </div>
          )}
        </div>

        {/* Meeting Types / Calendly Links */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div>
              <h2 style={styles.cardTitle}>📅 Meeting Types</h2>
              <p style={styles.cardSubtitle}>Add multiple Calendly links for different meeting types</p>
            </div>
          </div>

          {/* Existing meeting types */}
          {settings?.calendlyLinks?.length > 0 && (
            <div style={styles.meetingTypesList}>
              {settings.calendlyLinks.map((link, index) => (
                <div key={index} style={styles.meetingTypeRow}>
                  <div style={styles.meetingTypeInfo}>
                    <div style={styles.meetingTypeLabel}>
                      {link.label}
                      {link.isDefault && <span style={styles.defaultBadge}>Default</span>}
                      {!link.active && <span style={styles.inactiveBadge}>Inactive</span>}
                    </div>
                    <div style={styles.meetingTypeUrl}>{link.url}</div>
                    <div style={styles.meetingTypeDuration}>{link.duration} minutes</div>
                  </div>
                  <div style={styles.meetingTypeActions}>
                    {!link.isDefault && (
                      <button
                        style={styles.btnSetDefault}
                        onClick={async () => {
                          try {
                            await axios.put(`/settings/meeting-types/${index}`, { isDefault: true });
                            setMessage({ type: 'success', text: 'Default meeting type updated' });
                            fetchSettings();
                          } catch (err) {
                            setMessage({ type: 'error', text: 'Failed to update' });
                          }
                        }}
                      >
                        Set Default
                      </button>
                    )}
                    <button
                      style={styles.btnDeleteMeeting}
                      onClick={async () => {
                        if (!window.confirm('Remove this meeting type?')) return;
                        try {
                          await axios.delete(`/settings/meeting-types/${index}`);
                          setMessage({ type: 'success', text: 'Meeting type removed' });
                          fetchSettings();
                        } catch (err) {
                          setMessage({ type: 'error', text: 'Failed to remove' });
                        }
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add new meeting type form */}
          <div style={styles.addMeetingForm}>
            <h3 style={styles.addMeetingTitle}>
              {settings?.calendlyLinks?.length > 0 ? 'Add Another Meeting Type' : 'Add Your First Meeting Type'}
            </h3>
            <form onSubmit={async (e) => {
              e.preventDefault();
              const form = e.target;
              setMessage({ type: '', text: '' });
              try {
                const response = await axios.post('/settings/meeting-types', {
                  label: form.meetingLabel.value,
                  url: form.meetingUrl.value,
                  duration: parseInt(form.meetingDuration.value),
                  isDefault: form.isDefault.checked,
                });
                if (response.data.success) {
                  setMessage({ type: 'success', text: 'Meeting type added!' });
                  form.reset();
                  fetchSettings();
                }
              } catch (err) {
                setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to add' });
              }
            }}>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Meeting Label *</label>
                  <input
                    name="meetingLabel"
                    type="text"
                    style={styles.input}
                    placeholder="e.g. 30 Minute Consultation"
                    required
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Duration (minutes)</label>
                  <input
                    name="meetingDuration"
                    type="number"
                    style={styles.input}
                    defaultValue="30"
                    min="15"
                    max="120"
                  />
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Calendly URL *</label>
                <input
                  name="meetingUrl"
                  type="url"
                  style={styles.input}
                  placeholder="https://calendly.com/smoss/30min"
                  required
                />
              </div>
              <div style={styles.checkboxGroup}>
                <input name="isDefault" type="checkbox" id="isDefault" defaultChecked={settings?.calendlyLinks?.length === 0} />
                <label htmlFor="isDefault" style={styles.checkboxLabel}>
                  Use as default in report emails
                </label>
              </div>
              <button type="submit" style={styles.btnConnect}>
                Add Meeting Type
              </button>
            </form>
          </div>
        </div>
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div>
              <h2 style={styles.cardTitle}>⚙️ General Settings</h2>
              <p style={styles.cardSubtitle}>Configure your platform details</p>
            </div>
          </div>

          <form onSubmit={handleSaveGeneral}>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Assessment Price ($)</label>
                <input
                  type="number"
                  style={styles.input}
                  value={generalForm.assessmentPrice}
                  onChange={e => setGeneralForm(prev => ({ ...prev, assessmentPrice: Number(e.target.value) }))}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Admin Notification Emails</label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="steve@execspringboard.com, second@example.com"
                  value={generalForm.adminEmail}
                  onChange={e => setGeneralForm(prev => ({ ...prev, adminEmail: e.target.value }))}
                />
                <p style={{ fontSize: '12px', color: 'var(--color-text-light)', marginTop: '6px' }}>
                  Separate multiple addresses with commas, for example your own address and a
                  marketing inbox. Everyone listed receives every notification: comfort zone
                  assessment submissions, feedback survey responses, and any new form added later.
                </p>
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Calendly Link (for report delivery emails)</label>
              <input
                type="url"
                style={styles.input}
                placeholder="https://calendly.com/smoss-1/30min"
                value={generalForm.calendlyLink}
                onChange={e => setGeneralForm(prev => ({ ...prev, calendlyLink: e.target.value }))}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Report Delivery Message (shown to user after submission)</label>
              <textarea
                style={styles.textarea}
                value={generalForm.reportDeliveryMessage}
                onChange={e => setGeneralForm(prev => ({ ...prev, reportDeliveryMessage: e.target.value }))}
                rows={3}
              />
            </div>

            <button type="submit" style={styles.btnSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </form>
        </div>

        {/* Email Configuration */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div>
              <h2 style={styles.cardTitle}>📧 Email Configuration</h2>
              <p style={styles.cardSubtitle}>Configure SMTP to send emails from the platform</p>
            </div>
            <div style={{
              ...styles.statusBadge,
              ...(settings?.smtp?.configured ? styles.statusConnected : styles.statusDisconnected)
            }}>
              {settings?.smtp?.configured ? '● Connected' : '○ Not Connected'}
            </div>
          </div>

          {settings?.smtp?.configured ? (
            <div style={styles.connectedBox}>
              <div style={styles.connectedInfo}>
                <div style={styles.connectedRow}>
                  <span style={styles.connectedLabel}>Host</span>
                  <span style={styles.connectedValue}>{settings.smtp.host}</span>
                </div>
                <div style={styles.connectedRow}>
                  <span style={styles.connectedLabel}>User</span>
                  <span style={styles.connectedValue}>{settings.smtp.user}</span>
                </div>
                <div style={styles.connectedRow}>
                  <span style={styles.connectedLabel}>From</span>
                  <span style={styles.connectedValue}>{settings.smtp.fromName}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
                <button
                  style={styles.btnConnect}
                  onClick={async () => {
                    const email = window.prompt('Send test email to:', admin?.email);
                    if (!email) return;
                    try {
                      await axios.post('/settings/smtp/test', { toEmail: email });
                      setMessage({ type: 'success', text: `Test email sent to ${email}` });
                    } catch (err) {
                      setMessage({ type: 'error', text: 'Test email failed' });
                    }
                  }}
                >
                  Send Test Email
                </button>
                <button
                  style={styles.btnDanger}
                  onClick={async () => {
                    if (!window.confirm('Disconnect email?')) return;
                    try {
                      await axios.post('/settings/smtp/disconnect');
                      setMessage({ type: 'success', text: 'Email disconnected' });
                      fetchSettings();
                    } catch (err) {
                      setMessage({ type: 'error', text: 'Failed to disconnect' });
                    }
                  }}
                >
                  Disconnect
                </button>
              </div>
            </div>
          ) : (
            <div style={styles.connectOption}>
              <p style={styles.optionDesc}>
                Enter your SMTP email credentials. For Gmail use an <strong>App Password</strong> not your regular password.
                Go to Google Account → Security → 2-Step Verification → App Passwords to generate one.
              </p>
              <form onSubmit={async (e) => {
                e.preventDefault();
                const form = e.target;
                setMessage({ type: '', text: '' });
                try {
                  const response = await axios.post('/settings/smtp/configure', {
                    host: form.smtpHost.value,
                    port: form.smtpPort.value,
                    user: form.smtpUser.value,
                    pass: form.smtpPass.value,
                    fromName: form.fromName.value,
                    fromEmail: form.fromEmail.value,
                  });
                  if (response.data.success) {
                    setMessage({ type: 'success', text: 'Email configured successfully!' });
                    fetchSettings();
                  }
                } catch (err) {
                  setMessage({ type: 'error', text: err.response?.data?.error || 'Configuration failed' });
                }
              }}>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>SMTP Host</label>
                    <input name="smtpHost" type="text" style={styles.input} defaultValue="smtp.gmail.com" required />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>SMTP Port</label>
                    <input name="smtpPort" type="number" style={styles.input} defaultValue="587" required />
                  </div>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Email Address *</label>
                  <input name="smtpUser" type="email" style={styles.input} placeholder="steve@execspringboard.com" required />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>App Password *</label>
                  <input name="smtpPass" type="password" style={styles.input} placeholder="Gmail App Password (16 characters)" required />
                  <p style={styles.fieldNote}>Use a Gmail App Password, not your regular Gmail password.</p>
                </div>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>From Name</label>
                    <input name="fromName" type="text" style={styles.input} defaultValue="Executive Springboard" />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>From Email</label>
                    <input name="fromEmail" type="email" style={styles.input} placeholder="noreply@execspringboard.com" />
                  </div>
                </div>
                <button type="submit" style={styles.btnConnect}>
                  Configure Email
                </button>
              </form>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

const styles = {
  page: { display: 'flex', minHeight: '100vh', fontFamily: 'var(--font-primary)' },
  loadingPage: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  spinner: { width: '40px', height: '40px', border: '4px solid var(--color-light-gray)', borderTopColor: 'var(--color-teal)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },

  // Sidebar
  sidebar: { width: '240px', backgroundColor: 'var(--color-primary)', display: 'flex', flexDirection: 'column', padding: '24px 0', flexShrink: 0 },
  sidebarLogo: { display: 'flex', flexDirection: 'column', gap: '2px', padding: '0 20px 24px', borderBottom: '1px solid rgba(255,255,255,0.1)' },
  logoExec: { fontSize: '9px', fontWeight: '700', letterSpacing: '3px', color: 'var(--color-teal)', lineHeight: 1 },
  logoSpring: { fontSize: '18px', fontWeight: '700', color: '#fff', lineHeight: 1 },
  nav: { flex: 1, padding: '16px 0' },
  navItem: { width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 20px', background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: '14px', cursor: 'pointer', textAlign: 'left' },
  navItemActive: { backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff' },
  logoutBtn: { margin: '0 16px', padding: '10px', backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: 'rgba(255,255,255,0.5)', fontSize: '13px', cursor: 'pointer' },

  // Main
  main: { flex: 1, backgroundColor: 'var(--color-dashboard-bg)', padding: '32px', overflowY: 'auto' },
  mainHeader: { marginBottom: '28px' },
  pageTitle: { fontSize: '26px', fontWeight: '700', color: 'var(--color-primary)', marginBottom: '4px' },
  pageSubtitle: { fontSize: '14px', color: 'var(--color-text-light)' },

  messageBox: { padding: '14px 18px', borderRadius: '10px', fontSize: '14px', marginBottom: '20px' },
  messageSuccess: { backgroundColor: 'var(--color-success-bg)', color: 'var(--color-success)', border: '1px solid #A9DFBF' },
  messageError: { backgroundColor: 'var(--color-error-bg)', color: 'var(--color-error)', border: '1px solid #F5C6CB' },

  card: { backgroundColor: '#fff', borderRadius: '12px', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-sm)', padding: '28px', marginBottom: '24px' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  cardTitle: { fontSize: '18px', fontWeight: '600', color: 'var(--color-primary)', marginBottom: '4px' },
  cardSubtitle: { fontSize: '13px', color: 'var(--color-text-light)' },

  statusBadge: { padding: '6px 12px', borderRadius: '100px', fontSize: '12px', fontWeight: '500' },
  statusConnected: { backgroundColor: 'var(--color-success-bg)', color: 'var(--color-success)' },
  statusDisconnected: { backgroundColor: 'var(--color-light-gray)', color: 'var(--color-medium-gray)' },

  connectedBox: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'var(--color-off-white)', borderRadius: '10px', padding: '20px', flexWrap: 'wrap', gap: '16px' },
  connectedInfo: { display: 'flex', flexDirection: 'column', gap: '8px' },
  connectedRow: { display: 'flex', gap: '12px', fontSize: '14px' },
  connectedLabel: { color: 'var(--color-text-light)', width: '80px', flexShrink: 0 },
  connectedValue: { color: 'var(--color-text)', fontWeight: '500' },

  connectOption: { backgroundColor: 'var(--color-off-white)', borderRadius: '10px', padding: '24px', marginBottom: '16px' },
  connectOptionAlt: { backgroundColor: 'var(--color-off-white)', borderRadius: '10px', padding: '24px', opacity: 0.7 },
  optionTitle: { fontSize: '15px', fontWeight: '600', color: 'var(--color-primary)', marginBottom: '8px' },
  optionDesc: { fontSize: '13px', color: 'var(--color-text-light)', lineHeight: '1.6', marginBottom: '20px' },

  formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  formGroup: { marginBottom: '16px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--color-text)', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 14px', border: '1.5px solid var(--color-border)', borderRadius: '8px', fontSize: '14px', color: 'var(--color-text)', outline: 'none', boxSizing: 'border-box' },
  select: { width: '100%', padding: '10px 14px', border: '1.5px solid var(--color-border)', borderRadius: '8px', fontSize: '14px', color: 'var(--color-text)', outline: 'none', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '10px 14px', border: '1.5px solid var(--color-border)', borderRadius: '8px', fontSize: '14px', color: 'var(--color-text)', outline: 'none', boxSizing: 'border-box', resize: 'vertical' },
  fieldNote: { fontSize: '11px', color: 'var(--color-text-light)', marginTop: '4px' },

  btnConnect: { backgroundColor: 'var(--color-teal)', color: '#fff', padding: '12px 28px', borderRadius: '8px', border: 'none', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  btnPaypalOAuth: { backgroundColor: '#0070BA', color: '#fff', padding: '12px 28px', borderRadius: '8px', border: 'none', fontSize: '14px', fontWeight: '600', cursor: 'not-allowed', opacity: 0.6, display: 'flex', alignItems: 'center', gap: '8px' },
  btnSave: { backgroundColor: 'var(--color-primary)', color: '#fff', padding: '12px 28px', borderRadius: '8px', border: 'none', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  btnDanger: { backgroundColor: 'var(--color-error-bg)', color: 'var(--color-error)', padding: '10px 20px', borderRadius: '8px', border: '1px solid var(--color-error)', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  infoBox: { backgroundColor: 'var(--color-off-white)', borderRadius: '10px', padding: '20px' },
  infoText: { fontSize: '14px', color: 'var(--color-text)', lineHeight: '1.6', marginBottom: '16px' },
  codeBlock: { backgroundColor: '#1A2B42', color: '#1B9AAA', padding: '16px 20px', borderRadius: '8px', fontSize: '13px', lineHeight: '2', marginBottom: '12px', fontFamily: 'monospace' },
  infoNote: { fontSize: '12px', color: 'var(--color-text-light)', lineHeight: '1.6' },
  meetingTypesList: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' },
  meetingTypeRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'var(--color-off-white)', borderRadius: '10px', padding: '14px 16px', flexWrap: 'wrap', gap: '10px' },
  meetingTypeInfo: { flex: 1 },
  meetingTypeLabel: { fontSize: '14px', fontWeight: '600', color: 'var(--color-primary)', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' },
  meetingTypeUrl: { fontSize: '12px', color: 'var(--color-teal)', marginBottom: '2px' },
  meetingTypeDuration: { fontSize: '11px', color: 'var(--color-text-light)' },
  meetingTypeActions: { display: 'flex', gap: '8px' },
  defaultBadge: { backgroundColor: '#E8F8EF', color: '#1E8449', padding: '2px 8px', borderRadius: '100px', fontSize: '11px', fontWeight: '500' },
  inactiveBadge: { backgroundColor: '#F4F6F9', color: '#7F8C8D', padding: '2px 8px', borderRadius: '100px', fontSize: '11px', fontWeight: '500' },
  btnSetDefault: { backgroundColor: 'var(--color-info-bg)', color: 'var(--color-teal)', padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--color-teal)', fontSize: '12px', cursor: 'pointer' },
  btnDeleteMeeting: { backgroundColor: 'var(--color-error-bg)', color: 'var(--color-error)', padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--color-error)', fontSize: '12px', cursor: 'pointer' },
  addMeetingForm: { backgroundColor: 'var(--color-off-white)', borderRadius: '10px', padding: '20px' },
  addMeetingTitle: { fontSize: '14px', fontWeight: '600', color: 'var(--color-primary)', marginBottom: '16px' },
  checkboxGroup: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' },
  checkboxLabel: { fontSize: '14px', color: 'var(--color-text)', cursor: 'pointer' },
};

export default Settings;
